import {FC, JSXElementConstructor, ReactElement} from "react";
import {useTranslation} from "react-i18next";
import {Popover} from "@interstate/components/Popover";
import {OperationalEntity} from "@common-core/coat-operational-hierarchy-appsync-model";
import {useNavigation} from "../../navigation";
import {EntitySourceType} from "../EntitySourceType";
import "./OperationalEntityPopover.scss";

export interface OperationalEntityPopoverProps {
  caId: string;
  operationalEntities?: OperationalEntity[];
  children: ReactElement<any, string | JSXElementConstructor<any>>;
}

export const OperationalEntityPopover: FC<OperationalEntityPopoverProps> = ({
  caId,
  operationalEntities = [],
  children
}) => {
  const {t} = useTranslation();
  const {navigateToEntity} = useNavigation();

  const handleEntityClick = (entityId: string, event: React.MouseEvent) => {
    navigateToEntity(entityId, EntitySourceType.OPERATIONAL);
    // Since the popover renders over the top of the hierarchy of clients, which are themselves clickable,
    // we stopPropagation() to prevent the client from being accidentally clicked when we're clicking on an OE.
    event.stopPropagation();
  };

  return (
    <Popover
      id={`operational-entities-popover-${caId}`}
      data-testid={`operational-entities-popover-${caId}`}
      className={"operational-entities-popover"}
      trigger={"hover"}
      position={"left"}
      popoverContent={
        <div className="operational-entity-popover-content">
          <div className="operational-entity-header">
            <h4 className="operational-entity-title">
              {t("client-hierarchy.popover.operational-entities.title")}
            </h4>
          </div>

          <div className="operational-entities-container">
            {operationalEntities.map(operationalEntity => (
              <div
                key={operationalEntity.id}
                className={`operational-entity-item clickable`}
                id={`operational-entity-item-${operationalEntity.id}`}
                data-testid={`operational-entity-item-${operationalEntity.id}`}
                onClick={event =>
                  handleEntityClick(operationalEntity.id, event)
                }>
                <div className="operational-entity-info">
                  <div className="operational-entity-dba-name">
                    <strong>
                      {operationalEntity.organizationInformation.dbaName}
                    </strong>
                  </div>
                  <div className="operational-entity-id">
                    {operationalEntity.id}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      }>
      {children}
    </Popover>
  );
};
